/**
 * Formulário de um pedido de compra, em cartões: dados do pedido,
 * classificação e itens, o financeiro de cada item, observações e aprovação.
 *
 * Papel: preenche, valida e grava o pedido e a lista de itens. Cada item tem
 * foto, descrição, quantidade, fornecedor e valor unitário no cartão de itens,
 * e um cartão "Financeiro" próprio com categoria e subcategoria financeiras,
 * forma de pagamento, situação do lançamento, data, desconto, acréscimo e
 * parcelamento — é o que vira despesa quando o pedido chega a `comprado`.
 *
 * Como usar: quem monta passa `pedido` para editar ou `null` para criar, e
 * deverá dar ao componente uma `key` diferente por registro. Casa, número e
 * data de um pedido novo chegam prontos em `sugestao`, e as casas em `casas`,
 * porque viram `defaultValues` do `useForm`, lidos **uma única vez, no primeiro
 * render**: o react-hook-form não reage a uma prop que muda depois de o
 * formulário montar. Quem monta precisa esperar as consultas de que esses
 * valores dependem antes de montar este componente — é o que
 * `PaginaFormularioPedido` faz; montá-lo cedo demais trava o número e a casa no
 * que existia antes da resposta.
 *
 * Campo editável tem fundo branco (`EDITAVEL`); o que não se edita aqui tem
 * fundo cinza e cursor de bloqueio (`SOMENTE_LEITURA`): os espelhos de
 * fornecedor e descrição em cada cartão financeiro, e a linha da decisão no
 * cartão "Aprovação", que carrega quem aprovou, quando, quem comprou e o motivo
 * da recusa — todos só mudam pelas caixas de decisão. O cartão só aparece
 * depois de uma decisão, e por isso nunca num pedido novo: aprovador e
 * comprador não têm campo nesta tela, e quem os preenche é o quadro, ao tirar
 * o pedido da aprovação e do processo de compra. Número e data da solicitação
 * se editam nos dois modos, e deverão continuar assim: `numero` não é único, o
 * que chega em `sugestao` é uma contagem que repete número depois de uma
 * exclusão, e um pedido pode ser registrado com data passada.
 *
 * RESIDÊNCIA oferece um `Chip` por casa de `casas` — quem monta já as passa
 * pela regra das casas que recebem pedido — e, por último,
 * `ROTULO_SEM_RESIDENCIA`, que grava `residenciaId` nulo — o pedido que não é de
 * casa nenhuma, cuja despesa cai no caixa quando ele vira compra. O `Chip` diz
 * o nome da casa seguido de cidade e UF, e só o nome quando a casa chega sem
 * as duas. O campo sem
 * escolha nenhuma guarda a string vazia, e não o nulo: é a string vazia que o
 * schema recusa, e o nulo que ele aceita.
 *
 * Mudar a etapa para `aprovado`, `reprovado`, ou de `em_processo_de_compra`
 * para `comprado`, abre a mesma caixa que o quadro usa; a resposta muda os
 * campos na tela, e a gravação espera o "Salvar pedido de compra". As demais
 * etapas mudam direto. A de `em_cotacao` para `em_aprovacao` muda direto e
 * chama `onSairDaCotacao` com os valores já na etapa nova — é por ele que a
 * tela imprime a folha de aprovação do rascunho, sem gravar.
 *
 * A `ref`, quando alguém a passa, recebe um `AlcaDoFormularioPedido` com
 * `obterValores`: é o que `PaginaFormularioPedido` usa para duplicar o que está
 * na tela, edição não salva inclusive.
 *
 * **A imagem de um item já gravado não é recarregada ao abrir.** O botão "Ver
 * imagem" a busca sob demanda, e o `id` da linha é o que diz de qual item ela
 * é. Escolher um arquivo novo substitui a foto ao salvar; não escolher nada
 * preserva a gravada, porque `foto` ausente significa "não mexa" para a API.
 * Um arquivo maior do que `fotoDeEntradaDoPedidoSchema` aceita é recusado assim
 * que escolhido, com a mensagem do próprio schema.
 *
 * O total é recalculado a cada digitação por `totalDoPedido`, a mesma função
 * que o quadro e a tabela de finalizados usam, e o valor final de cada item
 * por `valorFinalDoItem`. Nenhum dos dois é campo, e nenhum é enviado.
 *
 * **A categoria financeira de cada item é exigida na virada para `comprado`**,
 * e só nela: a caixa de compra continua aberta com o aviso, e o "Salvar"
 * recusa com `AVISO_DE_CATEGORIA_PENDENTE`, marcando o campo de cada item
 * pendente. Os selects oferecem as categorias de despesa de `classificacao`, e
 * trocar a categoria limpa a subcategoria, que seria de outra. Digitar no
 * "Nome do fornecedor" um favorecido do caixa, ou um padrão de favorecido, e
 * sair do campo aplica ao item a categoria, a subcategoria e a forma de
 * pagamento dele, por `classificacaoDoFornecedor`.
 */
import {
  type ChangeEvent,
  type ReactNode,
  forwardRef,
  useId,
  useImperativeHandle,
  useState,
} from 'react';
import {
  AVISO_DE_CATEGORIA_PENDENTE,
  CANAL_ORIGEM_PEDIDO,
  CATEGORIA_PEDIDO_COMPRA,
  type ClassificacaoFinanceira,
  FORMA_PAGAMENTO_PEDIDO,
  INTERVALO_PARCELAMENTO,
  type PedidoCompra,
  STATUS_FINANCEIRO_PEDIDO,
  STATUS_PEDIDO_COMPRA,
  type StatusPedidoCompra,
  classificacaoDoFornecedor,
  dinheiroDoCampo,
  fotoDeEntradaDoPedidoSchema,
  novoPedidoCompraSchema,
  posicoesSemCategoria,
  totalBrutoDoPedido,
  totalDoPedido,
  valorFinalDoItem,
  viraComprado,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, type FieldPath, useFieldArray, useForm, useWatch } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { formatarData, formatarDinheiro } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import { Button } from '@/shared/ui/button';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { Checkbox } from '@/shared/ui/checkbox';
import { Chip } from '@/shared/ui/chip';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { Vazio } from '@/shared/ui/vazio';
import {
  AVISO_DA_COMPRA_SEM_CATEGORIA,
  CAMPO_SEM_CATEGORIA,
  opcoesDeCategoria,
  opcoesDeSubcategoria,
} from '../classificacao';
import { useFotoDoItem, useSalvarPedido } from '../hooks/usar-compras';
import {
  type DespesaAvisada,
  despesasAvisadas,
  lancaNaVirada,
} from '../lancado-no-financeiro';
import {
  ROTULO_SEM_RESIDENCIA,
  ROTULOS_CANAL,
  ROTULOS_CATEGORIA,
  ROTULOS_INTERVALO,
  ROTULOS_PAGAMENTO,
  ROTULOS_STATUS,
  ROTULOS_STATUS_FINANCEIRO,
} from '../rotulos';
import {
  ITEM_EM_BRANCO,
  type ItemNoFormulario,
  PEDIDO_EM_BRANCO,
  type PedidoNoFormulario,
} from '../types';
import { CaixaDeAprovacao, CaixaDeCompra, CaixaDeReprovacao } from './caixas-de-decisao';

/** Os valores que um pedido novo recebe já calculados por quem monta. */
export interface SugestaoDoNovoPedido {
  /** A casa pré-escolhida, ou a string vazia quando não há nenhuma a sugerir. */
  residenciaId: string;
  /** O número por contagem progressiva do ano, `PC-NNN/AAAA`. */
  numero: string;
  /** A data da solicitação, em ISO. */
  data: string;
}

/**
 * Uma casa oferecida em `Chip`: a residência cadastrada, com cidade e UF, ou a
 * casa que um pedido traz, só com o nome.
 */
export interface CasaDoPedido {
  id: string;
  nome: string;
  cidade?: string;
  uf?: string;
}

interface FormularioPedidoProps {
  /** O pedido a editar, ou `null` para criar um novo. */
  pedido: PedidoCompra | null;
  /** Casa, número e data de um pedido novo; ignorada ao editar. */
  sugestao: SugestaoDoNovoPedido;
  /** As casas que o pedido pode receber, oferecidas em `Chip`, na ordem da lista. */
  casas: readonly CasaDoPedido[];
  /** Os fornecedores já registrados e os favorecidos do caixa, oferecidos como sugestão. */
  fornecedores: readonly string[];
  /**
   * As categorias de despesa que os selects de cada item oferecem, e os
   * favorecidos e padrões cujo perfil o "Nome do fornecedor" aplica ao item.
   */
  classificacao: ClassificacaoFinanceira;
  /** Os solicitantes já registrados, oferecidos como sugestão. */
  solicitantes: readonly string[];
  /**
   * Chamado quando o `Chip` de etapa tira o rascunho de `em_cotacao` para
   * `em_aprovacao`, com os valores já na etapa nova; nada é gravado.
   */
  onSairDaCotacao?: (valores: PedidoNoFormulario) => void;
  /**
   * Chamado depois de gravar, com as despesas que a gravação lançou no caixa —
   * a lista vazia quando ela não levou o pedido a `comprado`, ou quando ele já
   * tinha despesa.
   */
  onSalvo: (despesasLancadas: DespesaAvisada[]) => void;
  onCancelar: () => void;
}

/**
 * O que `FormularioPedido` expõe a quem monta, pela `ref`.
 *
 * Existe porque "Duplicar", em `PaginaFormularioPedido`, precisa copiar o que
 * está na tela — inclusive edição que ninguém salvou ainda —, e o estado do
 * `useForm` mora dentro deste componente, fora do alcance da página.
 */
export interface AlcaDoFormularioPedido {
  /** Os valores do formulário no instante da chamada, antes do schema normalizar. */
  obterValores: () => PedidoNoFormulario;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<PedidoNoFormulario> | undefined> = {
  residenciaId: 'residenciaId',
  numero: 'numero',
  data: 'data',
  status: 'status',
  solicitante: 'solicitante',
  canalOrigem: 'canalOrigem',
  categoria: 'categoria',
  observacao: 'observacao',
  aprovador: 'aprovador',
  dataAprovacao: 'dataAprovacao',
  compradoPor: 'compradoPor',
  motivoReprovacao: 'motivoReprovacao',
  itens: 'itens',
};

/** Fundo do campo que se edita nesta tela. */
const EDITAVEL = 'bg-superficie';

/** Fundo, cor e cursor do que esta tela mostra sem deixar editar. */
const SOMENTE_LEITURA = 'cursor-not-allowed bg-superficie-2 text-texto-fraco';

/** O `<select>` nativo, no mesmo desenho do `Input`. */
const CLASSE_DO_SELECT =
  'w-full min-w-0 rounded-controle border border-linha-forte px-3 py-2.5 text-sm text-foreground outline-none focus-visible:border-brass disabled:cursor-not-allowed disabled:opacity-50';

/** Qual caixa de decisão o `Chip` de etapa abriu. */
type CaixaAberta = 'nenhuma' | 'aprovacao' | 'reprovacao' | 'compra';

/**
 * O caminho de um campo de item dentro do formulário.
 *
 * Existe para o tipo do caminho sobreviver: interpolar o índice já convertido em
 * texto produziria `itens.${string}.descricao`, que o react-hook-form não
 * reconhece, e obrigaria cada campo a uma asserção.
 *
 * @param indice - A posição da linha na lista de itens.
 * @param campo - O campo daquele item.
 * @returns O caminho que `Controller` aceita em `name`.
 */
function caminhoDoItem<C extends keyof ItemNoFormulario>(indice: number, campo: C) {
  return `itens.${indice}.${campo}` as const;
}

/**
 * O texto de um valor do formulário que pode ter chegado como número, texto ou
 * nada.
 *
 * @param valor - O valor guardado pelo react-hook-form.
 * @returns O texto para o campo, vazio quando não há valor.
 */
function textoDoCampo(valor: unknown): string {
  if (typeof valor === 'string') return valor;
  if (typeof valor === 'number') return String(valor);
  return '';
}

/**
 * Lê o arquivo escolhido como imagem em base64, valida contra
 * `fotoDeEntradaDoPedidoSchema` e entrega o resultado ao formulário.
 *
 * Quem chama é a caixa de foto de cada item, ao trocar o arquivo do `<input
 * type="file">` escondido dentro dela. A validação acontece aqui, e não só no
 * envio, para a recusa aparecer ao lado da foto no mesmo instante; um arquivo
 * que o navegador não consegue ler cai no mesmo `onInvalido`, com mensagem
 * própria.
 *
 * @param evento - O evento de troca do campo de arquivo.
 * @param onLido - Chamado com a imagem em base64, já validada.
 * @param onInvalido - Chamado com a mensagem a mostrar quando o arquivo não
 *   passa no schema ou não pôde ser lido.
 */
function handleArquivoDoItem(
  evento: ChangeEvent<HTMLInputElement>,
  onLido: (imagem: string) => void,
  onInvalido: (mensagem: string) => void,
) {
  const arquivo = evento.target.files?.[0];
  if (arquivo === undefined) return;

  const leitor = new FileReader();
  leitor.onload = () => {
    if (typeof leitor.result !== 'string') return;
    const validado = fotoDeEntradaDoPedidoSchema.safeParse(leitor.result);
    if (!validado.success) {
      onInvalido(validado.error.issues[0]?.message ?? 'Não foi possível usar esta imagem.');
      return;
    }
    onLido(leitor.result);
  };
  leitor.onerror = () => {
    onInvalido('Não foi possível ler o arquivo.');
  };
  leitor.readAsDataURL(arquivo);
}

/**
 * O botão que busca e mostra a imagem já gravada de um item.
 *
 * A imagem não vem com a listagem — são megabytes somados —, e por isso a
 * requisição só sai quando alguém clica. Depois de carregada ela fica no cache
 * pelo tempo que `useFotoDoItem` define, e reabrir não custa nova requisição.
 *
 * @param itemId - O item cuja imagem se quer.
 * @returns O botão, e a imagem abaixo dele depois de carregada.
 */
function ImagemDoItem({ itemId }: { itemId: string }) {
  const [pedida, setPedida] = useState(false);
  const consulta = useFotoDoItem(pedida ? itemId : null);
  const foto = consulta.data?.foto ?? null;

  return (
    <div>
      {!pedida && (
        <Button type="button" size="xs" variant="ghost" onClick={() => setPedida(true)}>
          Ver imagem
        </Button>
      )}
      {pedida && consulta.isPending && <p className="text-xs text-texto-fraco">Carregando…</p>}
      {pedida && consulta.error !== null && (
        <p className="text-xs text-perigo">Não foi possível carregar a imagem.</p>
      )}
      {foto !== null && (
        <img
          src={foto}
          alt="Imagem do item"
          className="mt-1 max-h-48 rounded-sm border border-border"
        />
      )}
    </div>
  );
}

/** A mensagem de erro de um campo, abaixo dele. */
function ErroDoCampo({ mensagem }: { mensagem: string | undefined }) {
  if (mensagem === undefined) return null;
  return <p className="text-xs text-perigo">{mensagem}</p>;
}

/**
 * Um cartão do formulário, com o título em caixa alta sobre um filete.
 *
 * @param titulo - O título do cartão; ausente no cartão que só avisa que falta
 *   item.
 * @param children - Os campos do cartão.
 * @returns A seção, nomeada pelo título para quem navega por região.
 */
function CartaoDoFormulario({ titulo, children }: { titulo?: string; children: ReactNode }) {
  const id = useId();
  return (
    <section
      aria-labelledby={titulo === undefined ? undefined : id}
      className="mb-5 rounded-md border border-linha-forte bg-superficie px-[22px] py-5"
    >
      {titulo !== undefined && (
        <h2
          id={id}
          className="mb-4 border-b border-linha-forte pb-2.5 font-sans text-[12.5px] font-semibold tracking-[0.04em] text-texto-medio uppercase"
        >
          {titulo}
        </h2>
      )}
      {children}
    </section>
  );
}

/**
 * Um campo com o rótulo mono acima e o erro do schema abaixo.
 *
 * @param rotulo - O rótulo, já na caixa em que aparece.
 * @param htmlFor - O `id` do campo que o rótulo nomeia.
 * @param erro - A mensagem do schema, ou `undefined`.
 * @param className - Classes de quem chama, para a posição na grade.
 * @param children - O campo.
 * @returns O grupo de rótulo, campo e erro.
 */
function CampoDoPedido({
  rotulo,
  htmlFor,
  erro,
  className,
  children,
}: {
  rotulo: string;
  htmlFor: string;
  erro?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>{rotulo}</Label>
      {children}
      <ErroDoCampo mensagem={erro} />
    </div>
  );
}

/**
 * Uma escolha de opção única desenhada em `Chip`, com o rótulo mono acima.
 *
 * @param rotulo - O rótulo do grupo, que também o nomeia para leitor de tela.
 * @param erro - A mensagem do schema, ou `undefined`.
 * @param children - Os `Chip`, um por opção.
 * @returns O grupo, com a margem de um bloco de campo.
 */
function GrupoDeChips({
  rotulo,
  erro,
  children,
}: {
  rotulo: string;
  erro?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="mb-4 last:mb-0">
      <span
        id={id}
        className="mb-2 block font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco"
      >
        {rotulo}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
      <ErroDoCampo mensagem={erro} />
    </div>
  );
}

/**
 * Um valor que esta tela só mostra, com a nota de onde ele vem.
 *
 * @param rotulo - O rótulo, em caixa alta.
 * @param valor - O que mostrar; o travessão quando vazio.
 * @param dica - De onde o valor vem, começando pelo cadeado.
 * @returns O bloco cinza de leitura, com a nota abaixo.
 */
function CampoEspelhado({
  rotulo,
  valor,
  dica,
}: {
  rotulo: string;
  valor: string;
  dica: string;
}) {
  return (
    <div className="mb-4 flex flex-col gap-1.5">
      <span className="font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
        {rotulo}
      </span>
      <div className={cn('rounded-controle px-3 py-2.5 font-sans text-sm', SOMENTE_LEITURA)}>
        {valor === '' ? '—' : valor}
      </div>
      <p className="mt-1 text-[11px] text-texto-fraco">{dica}</p>
    </div>
  );
}

/**
 * O formulário de um pedido.
 *
 * @param pedido - O registro a editar, ou `null`.
 * @param sugestao - Casa, número e data de um pedido novo, já calculados; o
 *   número e a data entram nos campos como ponto de partida, que a pessoa pode
 *   trocar antes de salvar.
 * @param casas - As casas que o pedido pode receber, oferecidas em `Chip`; quem
 *   monta já tirou delas o lugar que contém outra casa.
 * @param fornecedores - Nomes já usados e favorecidos do caixa, oferecidos pelo
 *   `datalist` de cada item.
 * @param classificacao - As categorias dos selects do cartão financeiro e os
 *   perfis que o "Nome do fornecedor" aplica; vazia enquanto a consulta não
 *   responde, e nesse intervalo nada é preenchido pelo fornecedor.
 * @param solicitantes - Nomes já usados, oferecidos da mesma forma.
 * @param onSairDaCotacao - Chamado na passagem do rascunho da cotação para a
 *   aprovação pelo `Chip`, para a tela gerar a folha de aprovação.
 * @param onSalvo - Chamado depois de gravar, para voltar à lista, com as
 *   despesas de `despesasAvisadas` quando a gravação levou a `comprado` um
 *   pedido sem despesa — `lancaNaVirada` — e a lista vazia nos demais casos.
 * @param onCancelar - Chamado ao desistir, sem gravar nada.
 * @param ref - Recebe `AlcaDoFormularioPedido`, com `obterValores` — a leitura
 *   dos campos por fora do envio, que "Duplicar" usa.
 * @returns Os cartões do pedido, as caixas de decisão da etapa e o rodapé.
 */
export const FormularioPedido = forwardRef<AlcaDoFormularioPedido, FormularioPedidoProps>(
  function FormularioPedido(
    {
      pedido,
      sugestao,
      casas,
      fornecedores,
      classificacao,
      solicitantes,
      onSairDaCotacao,
      onSalvo,
      onCancelar,
    },
    ref,
  ) {
    const [erroGeral, setErroGeral] = useState<string | null>(null);
    const [caixa, setCaixa] = useState<CaixaAberta>('nenhuma');
    const [erroDaCaixa, setErroDaCaixa] = useState<string | null>(null);
    const salvar = useSalvarPedido();

    const form = useForm<PedidoNoFormulario>({
      resolver: zodResolver(novoPedidoCompraSchema),
      defaultValues:
        pedido === null
          ? {
              ...PEDIDO_EM_BRANCO,
              residenciaId: sugestao.residenciaId,
              numero: sugestao.numero,
              data: sugestao.data,
            }
          : {
              residenciaId: pedido.residencia?.id ?? null,
              numero: pedido.numero,
              data: pedido.data,
              status: pedido.status,
              solicitante: pedido.solicitante,
              canalOrigem: pedido.canalOrigem,
              categoria: pedido.categoria,
              observacao: pedido.observacao ?? '',
              aprovador: pedido.aprovador ?? '',
              dataAprovacao: pedido.dataAprovacao ?? '',
              compradoPor: pedido.compradoPor ?? '',
              motivoReprovacao: pedido.motivoReprovacao ?? '',
              lancadoNoFinanceiro: pedido.lancadoNoFinanceiro,
              // A imagem fica de fora de propósito: ausente preserva a gravada, e
              // é o `id` que diz de qual item ela é.
              itens: pedido.itens.map((item) => ({
                id: item.id,
                descricao: item.descricao,
                quantidade: item.quantidade,
                valorUnitario: String(item.valorUnitario),
                desconto: String(item.desconto),
                acrescimo: String(item.acrescimo),
                fornecedor: item.fornecedor ?? '',
                categoriaFinanceira: item.categoriaFinanceira ?? '',
                subcategoriaFinanceira: item.subcategoriaFinanceira ?? '',
                formaPagamento: item.formaPagamento ?? undefined,
                statusFinanceiro: item.statusFinanceiro,
                dataCompra: item.dataCompra ?? '',
                parcelas: item.parcelas === null ? '' : String(item.parcelas),
                intervaloParcelamento: item.intervaloParcelamento ?? undefined,
              })),
            },
    });

    useImperativeHandle(ref, () => ({ obterValores: () => form.getValues() }), [form]);

    const linhas = useFieldArray({ control: form.control, name: 'itens' });
    const itensAoVivo = useWatch({ control: form.control, name: 'itens' }) ?? [];
    const status = useWatch({ control: form.control, name: 'status' });
    const residenciaId = useWatch({ control: form.control, name: 'residenciaId' });
    const canalOrigem = useWatch({ control: form.control, name: 'canalOrigem' });
    const categoria = useWatch({ control: form.control, name: 'categoria' });
    const aprovador = textoDoCampo(useWatch({ control: form.control, name: 'aprovador' }));
    const dataAprovacao = textoDoCampo(
      useWatch({ control: form.control, name: 'dataAprovacao' }),
    );
    const compradoPor = textoDoCampo(useWatch({ control: form.control, name: 'compradoPor' }));
    const motivoReprovacao = textoDoCampo(
      useWatch({ control: form.control, name: 'motivoReprovacao' }),
    );
    const lancadoNoFinanceiro = useWatch({
      control: form.control,
      name: 'lancadoNoFinanceiro',
    });
    const enviando = salvar.isPending;
    const erros = form.formState.errors;

    const paraSoma = itensAoVivo.map((item) => ({
      quantidade: dinheiroDoCampo(item?.quantidade),
      valorUnitario: dinheiroDoCampo(item?.valorUnitario),
      desconto: dinheiroDoCampo(item?.desconto),
      acrescimo: dinheiroDoCampo(item?.acrescimo),
    }));
    const total = totalDoPedido(paraSoma);
    const bruto = totalBrutoDoPedido(paraSoma);

    const handleSalvar = form.handleSubmit(
      async (dados) => {
        setErroGeral(null);
        const corpo = novoPedidoCompraSchema.parse(dados);
        if (
          viraComprado(pedido?.status ?? null, corpo.status) &&
          marcarItensSemCategoria(corpo.itens)
        ) {
          setErroGeral(AVISO_DE_CATEGORIA_PENDENTE);
          return;
        }
        try {
          await salvar.mutateAsync({ id: pedido?.id ?? null, dados: corpo });
          onSalvo(lancaNaVirada(pedido, corpo.status) ? despesasAvisadas(corpo) : []);
        } catch (erro) {
          setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
        }
      },
      () => setErroGeral('Corrija os campos marcados em vermelho antes de salvar.'),
    );

    /**
     * Marca com erro a categoria de cada item que ainda não a tem.
     *
     * @param itens - Os itens, na ordem do formulário.
     * @returns Verdadeiro quando algum item foi marcado, e a virada para
     *   `comprado` deverá esperar.
     */
    function marcarItensSemCategoria(
      itens: readonly { categoriaFinanceira?: string | null }[],
    ) {
      const pendentes = posicoesSemCategoria(itens);
      for (const posicao of pendentes) {
        form.setError(caminhoDoItem(posicao, 'categoriaFinanceira'), {
          message: CAMPO_SEM_CATEGORIA,
        });
      }
      return pendentes.length > 0;
    }

    /**
     * Aplica ao item o perfil do favorecido cujo nome acabou de ser digitado.
     *
     * Quem chama é o campo "Nome do fornecedor" de cada item, ao perder o
     * foco — e não a cada tecla, porque o nome digitado por cima de outro
     * passaria pelo perfil do mais curto e o deixaria no item. O perfil sem
     * categoria não mexe na categoria nem na subcategoria que já
     * estavam escolhidas; o que tem categoria e não tem subcategoria limpa a
     * subcategoria, que seria de outra categoria. A forma que o pedido não tem
     * deixa a do item como estava.
     *
     * @param indice - A posição do item.
     * @param nome - O nome como está no campo.
     */
    function handleFornecedor(indice: number, nome: string) {
      const perfil = classificacaoDoFornecedor(
        nome,
        classificacao.favorecidos,
        classificacao.padroes,
      );
      if (perfil === null) return;
      if (perfil.categoriaFinanceira !== null) {
        form.setValue(
          caminhoDoItem(indice, 'categoriaFinanceira'),
          perfil.categoriaFinanceira,
          {
            shouldDirty: true,
          },
        );
        form.setValue(
          caminhoDoItem(indice, 'subcategoriaFinanceira'),
          perfil.subcategoriaFinanceira ?? '',
          { shouldDirty: true },
        );
        form.clearErrors(caminhoDoItem(indice, 'categoriaFinanceira'));
      }
      if (perfil.formaPagamento !== null) {
        form.setValue(caminhoDoItem(indice, 'formaPagamento'), perfil.formaPagamento, {
          shouldDirty: true,
        });
      }
    }

    /**
     * Troca a etapa pelo `Chip`, ou abre a caixa de decisão que a troca exige.
     *
     * @param etapa - A etapa do `Chip` clicado.
     */
    function handleEtapa(etapa: StatusPedidoCompra) {
      if (etapa === 'aprovado') {
        setCaixa('aprovacao');
        return;
      }
      if (etapa === 'reprovado') {
        setCaixa('reprovacao');
        return;
      }
      if (etapa === 'comprado' && form.getValues('status') === 'em_processo_de_compra') {
        setErroDaCaixa(null);
        setCaixa('compra');
        return;
      }
      const saiDaCotacao =
        etapa === 'em_aprovacao' && form.getValues('status') === 'em_cotacao';
      form.setValue('status', etapa, { shouldDirty: true });
      if (saiDaCotacao) onSairDaCotacao?.(form.getValues());
    }

    /**
     * Registra na tela a aprovação que a caixa respondeu, com a data de hoje, e
     * leva o pedido ao processo de compra.
     *
     * @param nome - Quem aprovou.
     */
    function handleAprovado(nome: string) {
      form.setValue('aprovador', nome, { shouldDirty: true });
      form.setValue('dataAprovacao', hojeEmIso(), { shouldDirty: true });
      form.setValue('status', 'em_processo_de_compra', { shouldDirty: true });
      setCaixa('nenhuma');
    }

    /**
     * Registra na tela o motivo que a caixa respondeu, e encerra o pedido.
     *
     * @param motivo - Por que o pedido foi reprovado.
     */
    function handleReprovado(motivo: string) {
      form.setValue('motivoReprovacao', motivo, { shouldDirty: true });
      form.setValue('status', 'finalizado', { shouldDirty: true });
      setCaixa('nenhuma');
    }

    /**
     * Registra na tela quem comprou, e leva o pedido a comprado.
     *
     * Com item sem categoria a caixa continua aberta, com o aviso, e a
     * categoria de cada item pendente fica marcada no cartão financeiro dele.
     *
     * @param nome - Quem fez a compra.
     */
    function handleComprado(nome: string) {
      if (marcarItensSemCategoria(form.getValues('itens') ?? [])) {
        setErroDaCaixa(AVISO_DA_COMPRA_SEM_CATEGORIA);
        return;
      }
      form.setValue('compradoPor', nome, { shouldDirty: true });
      form.setValue('status', 'comprado', { shouldDirty: true });
      setCaixa('nenhuma');
    }

    const temDecisao = aprovador !== '' || motivoReprovacao !== '' || compradoPor !== '';
    const textoDaDecisao =
      motivoReprovacao !== ''
        ? `✕ Reprovado — ${motivoReprovacao}`
        : [
            aprovador === ''
              ? null
              : `✓ Aprovado por ${aprovador} em ${formatarData(dataAprovacao)}`,
            compradoPor === '' ? null : `Comprado por ${compradoPor}`,
          ]
            .filter((parte) => parte !== null)
            .join(' · ');

    return (
      <>
        <form onSubmit={(evento) => void handleSalvar(evento)}>
          <CartaoDoFormulario titulo="Dados do pedido">
            <div className="mb-4 grid grid-cols-3 gap-4">
              <CampoDoPedido
                rotulo="NÚMERO"
                htmlFor="pedido-numero"
                erro={erros.numero?.message}
              >
                <Controller
                  control={form.control}
                  name="numero"
                  render={({ field }) => (
                    <Input
                      id="pedido-numero"
                      {...field}
                      value={field.value ?? ''}
                      disabled={enviando}
                      className={EDITAVEL}
                    />
                  )}
                />
              </CampoDoPedido>
              <CampoDoPedido
                rotulo="DATA DA SOLICITAÇÃO"
                htmlFor="pedido-data"
                erro={erros.data?.message}
              >
                <Controller
                  control={form.control}
                  name="data"
                  render={({ field }) => (
                    <CampoMascarado
                      id="pedido-data"
                      formato="data"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={enviando}
                      className={cn(EDITAVEL, 'font-sans')}
                    />
                  )}
                />
              </CampoDoPedido>
              <CampoDoPedido
                rotulo="SOLICITANTE"
                htmlFor="pedido-solicitante"
                erro={erros.solicitante?.message}
              >
                <Controller
                  control={form.control}
                  name="solicitante"
                  render={({ field }) => (
                    <Input
                      id="pedido-solicitante"
                      list="solicitantes-conhecidos"
                      placeholder="Quem pediu a compra"
                      {...field}
                      value={field.value ?? ''}
                      disabled={enviando}
                      className={EDITAVEL}
                    />
                  )}
                />
                <datalist id="solicitantes-conhecidos">
                  {solicitantes.map((nome) => (
                    <option key={nome} value={nome} />
                  ))}
                </datalist>
              </CampoDoPedido>
            </div>

            <GrupoDeChips rotulo="STATUS" erro={erros.status?.message}>
              {STATUS_PEDIDO_COMPRA.map((etapa) => (
                <Chip
                  key={etapa}
                  ativo={status === etapa}
                  tom={
                    etapa === 'finalizado'
                      ? 'sucesso'
                      : etapa === 'reprovado'
                        ? 'perigo'
                        : 'padrao'
                  }
                  onClick={() => handleEtapa(etapa)}
                  disabled={enviando}
                >
                  {ROTULOS_STATUS[etapa]}
                </Chip>
              ))}
            </GrupoDeChips>

            {(motivoReprovacao !== '' || lancadoNoFinanceiro === true) && (
              <div className="flex flex-col gap-2.5">
                {motivoReprovacao !== '' && (
                  <div className="rounded-[4px] border border-perigo/30 bg-perigo/8 px-3 py-2.5 text-[12.5px] text-perigo">
                    <strong>Motivo da recusa:</strong> {motivoReprovacao}
                  </div>
                )}
                {lancadoNoFinanceiro === true && (
                  <div className="rounded-[4px] border border-sucesso/30 bg-sucesso/8 px-3 py-2.5 text-[12.5px] text-sucesso">
                    ✓ Lançamento confirmado no Financeiro
                  </div>
                )}
              </div>
            )}
          </CartaoDoFormulario>

          <CartaoDoFormulario titulo="Classificação e itens do pedido">
            <GrupoDeChips rotulo="RESIDÊNCIA" erro={erros.residenciaId?.message}>
              {casas.map((casa) => (
                <Chip
                  key={casa.id}
                  ativo={residenciaId === casa.id}
                  onClick={() =>
                    form.setValue('residenciaId', casa.id, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  disabled={enviando}
                >
                  {casa.cidade === undefined || casa.uf === undefined
                    ? casa.nome
                    : `${casa.nome} (${casa.cidade}, ${casa.uf})`}
                </Chip>
              ))}
              <Chip
                ativo={residenciaId === null}
                onClick={() =>
                  form.setValue('residenciaId', null, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                disabled={enviando}
              >
                {ROTULO_SEM_RESIDENCIA}
              </Chip>
            </GrupoDeChips>

            <GrupoDeChips rotulo="CANAL DE ORIGEM" erro={erros.canalOrigem?.message}>
              {CANAL_ORIGEM_PEDIDO.map((canal) => (
                <Chip
                  key={canal}
                  ativo={canalOrigem === canal}
                  onClick={() => form.setValue('canalOrigem', canal, { shouldDirty: true })}
                  disabled={enviando}
                >
                  {ROTULOS_CANAL[canal]}
                </Chip>
              ))}
            </GrupoDeChips>

            <GrupoDeChips rotulo="CATEGORIA" erro={erros.categoria?.message}>
              {CATEGORIA_PEDIDO_COMPRA.map((valor) => (
                <Chip
                  key={valor}
                  ativo={categoria === valor}
                  onClick={() => form.setValue('categoria', valor, { shouldDirty: true })}
                  disabled={enviando}
                >
                  {ROTULOS_CATEGORIA[valor]}
                </Chip>
              ))}
            </GrupoDeChips>

            <CabecalhoDeSecao
              titulo="Itens do pedido"
              className="mt-2"
              acoes={
                <Button
                  type="button"
                  className="print:hidden"
                  onClick={() => linhas.append(ITEM_EM_BRANCO)}
                  disabled={enviando}
                >
                  + Adicionar item
                </Button>
              }
            />

            {linhas.fields.length === 0 ? (
              <Vazio mensagem="Nenhum item adicionado ainda." className="mb-6 p-[30px]" />
            ) : (
              <ul className="mb-6 flex list-none flex-col gap-3.5 p-0">
                {linhas.fields.map((linha, indice) => {
                  const idDoItem = form.getValues(caminhoDoItem(indice, 'id'));
                  const gravado =
                    typeof idDoItem === 'string'
                      ? pedido?.itens.find((item) => item.id === idDoItem)
                      : undefined;
                  const n = String(indice + 1);

                  return (
                    <li
                      key={linha.id}
                      className="flex flex-col gap-4 rounded-md border border-linha-forte bg-superficie p-4 sm:flex-row sm:items-start"
                    >
                      <Controller
                        control={form.control}
                        name={caminhoDoItem(indice, 'foto')}
                        render={({ field, fieldState }) => (
                          <div className="shrink-0">
                            <label className="flex h-[88px] w-[88px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border border-dashed border-linha-forte p-1 text-center font-mono text-[10.5px] leading-snug text-texto-fraco transition-[border-color,color] duration-150 ease-in-out hover:border-brass hover:text-brass-light">
                              <input
                                ref={field.ref}
                                type="file"
                                accept="image/*"
                                aria-label={`Foto do item ${n}`}
                                className="hidden"
                                disabled={enviando}
                                onChange={(evento) =>
                                  handleArquivoDoItem(
                                    evento,
                                    (imagem) => {
                                      form.clearErrors(caminhoDoItem(indice, 'foto'));
                                      field.onChange(imagem);
                                    },
                                    (mensagem) =>
                                      form.setError(caminhoDoItem(indice, 'foto'), {
                                        message: mensagem,
                                      }),
                                  )
                                }
                              />
                              {typeof field.value === 'string' && field.value !== '' ? (
                                <img
                                  src={field.value}
                                  alt="Foto do item"
                                  className="h-full w-full object-cover"
                                />
                              ) : gravado?.temFoto === true ? (
                                <span>Foto salva. Clique para trocar.</span>
                              ) : (
                                <>
                                  <span>📷</span>
                                  <span>Clique para</span>
                                  <span>adicionar foto</span>
                                </>
                              )}
                            </label>
                            {fieldState.error !== undefined && (
                              <p className="mt-1 w-[88px] text-xs text-perigo">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        )}
                      />

                      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_140px]">
                        <Controller
                          control={form.control}
                          name={caminhoDoItem(indice, 'descricao')}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-2">
                              <Label htmlFor={`item-${n}-descricao`}>Descrição do item</Label>
                              <Textarea
                                id={`item-${n}-descricao`}
                                aria-label={`Descrição do item ${n}`}
                                placeholder="Descrição, marca, especificações..."
                                rows={2}
                                {...field}
                                value={textoDoCampo(field.value)}
                                disabled={enviando}
                                className={cn(EDITAVEL, 'py-[9px] text-[13.5px]')}
                              />
                              <ErroDoCampo mensagem={fieldState.error?.message} />
                            </div>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name={caminhoDoItem(indice, 'quantidade')}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-2">
                              <Label htmlFor={`item-${n}-quantidade`}>Qtd.</Label>
                              <Input
                                id={`item-${n}-quantidade`}
                                aria-label={`Quantidade do item ${n}`}
                                type="number"
                                min={1}
                                step={1}
                                {...field}
                                value={textoDoCampo(field.value)}
                                disabled={enviando}
                                className={cn(EDITAVEL, 'py-[9px] text-[13.5px]')}
                              />
                              <ErroDoCampo mensagem={fieldState.error?.message} />
                            </div>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name={caminhoDoItem(indice, 'fornecedor')}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-2">
                              <Label htmlFor={`item-${n}-fornecedor`}>
                                Nome do fornecedor
                              </Label>
                              <Input
                                id={`item-${n}-fornecedor`}
                                aria-label={`Fornecedor do item ${n}`}
                                list="fornecedores-conhecidos"
                                placeholder="Ex: nome da loja ou vendedor"
                                {...field}
                                onBlur={(evento) => {
                                  field.onBlur();
                                  handleFornecedor(indice, evento.target.value);
                                }}
                                value={textoDoCampo(field.value)}
                                disabled={enviando}
                                className={cn(EDITAVEL, 'py-[9px] text-[13.5px]')}
                              />
                              <ErroDoCampo mensagem={fieldState.error?.message} />
                            </div>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name={caminhoDoItem(indice, 'valorUnitario')}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-2">
                              <Label htmlFor={`item-${n}-valor`}>Valor unit. (R$)</Label>
                              <CampoMascarado
                                id={`item-${n}-valor`}
                                aria-label={`Valor unitário do item ${n}`}
                                formato="dinheiro"
                                placeholder="0,00"
                                value={textoDoCampo(field.value)}
                                onChange={field.onChange}
                                disabled={enviando}
                                className={cn(EDITAVEL, 'py-[9px] font-sans text-[13.5px]')}
                              />
                              <ErroDoCampo mensagem={fieldState.error?.message} />
                            </div>
                          )}
                        />
                        {gravado?.temFoto === true && (
                          <div className="sm:col-span-2">
                            <ImagemDoItem itemId={gravado.id} />
                          </div>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="destructive"
                        title="Remover item"
                        aria-label={`Remover item ${n}`}
                        className="h-fit self-start px-2.5 py-2 print:hidden"
                        onClick={() => linhas.remove(indice)}
                        disabled={enviando}
                      >
                        🗑
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}

            <datalist id="fornecedores-conhecidos">
              {fornecedores.map((nome) => (
                <option key={nome} value={nome} />
              ))}
            </datalist>

            <div className="-mt-1.5 flex items-end gap-4 sm:px-4">
              <span aria-hidden className="hidden w-[88px] shrink-0 sm:block" />
              <span aria-hidden className="flex-1" />
              <div className="w-[140px] shrink-0">
                <span className="mb-2 block font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
                  TOTAL DO PEDIDO
                </span>
                <span className="block font-mono text-[20px] font-semibold text-brass-light">
                  {formatarDinheiro(total)}
                </span>
                {bruto !== total && (
                  <span className="tabular block text-xs text-texto-fraco">
                    bruto {formatarDinheiro(bruto)}
                  </span>
                )}
              </div>
              <span
                aria-hidden
                className="invisible hidden h-fit shrink-0 px-2.5 py-2 text-xs sm:inline-block"
              >
                🗑
              </span>
            </div>

            <ErroDoCampo mensagem={erros.itens?.message ?? erros.itens?.root?.message} />
          </CartaoDoFormulario>

          {linhas.fields.length === 0 ? (
            <CartaoDoFormulario>
              <Vazio
                mensagem="Adicione ao menos um item ao pedido para configurar o financeiro."
                className="p-5"
              />
            </CartaoDoFormulario>
          ) : (
            linhas.fields.map((linha, indice) => {
              const item = itensAoVivo[indice];
              const descricao = textoDoCampo(item?.descricao);
              const n = String(indice + 1);
              const valorFinal = valorFinalDoItem({
                quantidade: dinheiroDoCampo(item?.quantidade),
                valorUnitario: dinheiroDoCampo(item?.valorUnitario),
                desconto: dinheiroDoCampo(item?.desconto),
                acrescimo: dinheiroDoCampo(item?.acrescimo),
              });
              const parcelado = textoDoCampo(item?.parcelas) !== '';

              return (
                <CartaoDoFormulario
                  key={linha.id}
                  titulo={`Financeiro — Item ${n}${
                    descricao === ''
                      ? ''
                      : `: ${descricao.length > 44 ? `${descricao.slice(0, 44)}…` : descricao}`
                  }`}
                >
                  <CampoEspelhado
                    rotulo="FORNECEDOR"
                    valor={textoDoCampo(item?.fornecedor).trim()}
                    dica={
                      '🔒 Preenchido a partir do "Nome do fornecedor" deste item, no card acima.'
                    }
                  />
                  <CampoEspelhado
                    rotulo="DESCRIÇÃO"
                    valor={descricao}
                    dica="🔒 Preenchido a partir da descrição deste item, no card acima."
                  />

                  <div className="mb-4 grid grid-cols-2 gap-4">
                    <Controller
                      control={form.control}
                      name={caminhoDoItem(indice, 'categoriaFinanceira')}
                      render={({ field, fieldState }) => (
                        <CampoDoPedido
                          rotulo="CATEGORIA (FINANCEIRO)"
                          htmlFor={`item-${n}-categoria`}
                          erro={fieldState.error?.message}
                        >
                          <select
                            id={`item-${n}-categoria`}
                            aria-label={`Categoria financeira do item ${n}`}
                            name={field.name}
                            ref={field.ref}
                            onBlur={field.onBlur}
                            value={textoDoCampo(field.value)}
                            onChange={(evento) => {
                              field.onChange(evento.target.value);
                              form.setValue(
                                caminhoDoItem(indice, 'subcategoriaFinanceira'),
                                '',
                                {
                                  shouldDirty: true,
                                },
                              );
                            }}
                            disabled={enviando}
                            className={cn(CLASSE_DO_SELECT, EDITAVEL)}
                          >
                            <option value="">— Selecionar —</option>
                            {opcoesDeCategoria(
                              classificacao.categorias,
                              textoDoCampo(field.value),
                            ).map((nome) => (
                              <option key={nome} value={nome}>
                                {nome}
                              </option>
                            ))}
                          </select>
                        </CampoDoPedido>
                      )}
                    />
                    <Controller
                      control={form.control}
                      name={caminhoDoItem(indice, 'subcategoriaFinanceira')}
                      render={({ field, fieldState }) => (
                        <CampoDoPedido
                          rotulo="SUBCATEGORIA (FINANCEIRO)"
                          htmlFor={`item-${n}-subcategoria`}
                          erro={fieldState.error?.message}
                        >
                          <select
                            id={`item-${n}-subcategoria`}
                            aria-label={`Subcategoria financeira do item ${n}`}
                            name={field.name}
                            ref={field.ref}
                            onBlur={field.onBlur}
                            value={textoDoCampo(field.value)}
                            onChange={(evento) => field.onChange(evento.target.value)}
                            disabled={enviando}
                            className={cn(CLASSE_DO_SELECT, EDITAVEL)}
                          >
                            <option value="">— Selecionar —</option>
                            {opcoesDeSubcategoria(
                              classificacao.categorias,
                              textoDoCampo(item?.categoriaFinanceira),
                              textoDoCampo(field.value),
                            ).map((nome) => (
                              <option key={nome} value={nome}>
                                {nome}
                              </option>
                            ))}
                          </select>
                        </CampoDoPedido>
                      )}
                    />
                  </div>

                  <Controller
                    control={form.control}
                    name={caminhoDoItem(indice, 'formaPagamento')}
                    render={({ field, fieldState }) => (
                      <GrupoDeChips
                        rotulo={`FORMA DE PAGAMENTO`}
                        erro={fieldState.error?.message}
                      >
                        {FORMA_PAGAMENTO_PEDIDO.map((forma) => (
                          <Chip
                            key={forma}
                            ativo={field.value === forma}
                            aria-label={`${ROTULOS_PAGAMENTO[forma]}, item ${n}`}
                            onClick={() => field.onChange(forma)}
                            disabled={enviando}
                          >
                            {ROTULOS_PAGAMENTO[forma]}
                          </Chip>
                        ))}
                      </GrupoDeChips>
                    )}
                  />

                  <Controller
                    control={form.control}
                    name={caminhoDoItem(indice, 'statusFinanceiro')}
                    render={({ field, fieldState }) => (
                      <GrupoDeChips
                        rotulo="STATUS DO LANÇAMENTO"
                        erro={fieldState.error?.message}
                      >
                        {STATUS_FINANCEIRO_PEDIDO.map((situacao) => (
                          <Chip
                            key={situacao}
                            ativo={field.value === situacao}
                            aria-label={`${ROTULOS_STATUS_FINANCEIRO[situacao]}, item ${n}`}
                            onClick={() => field.onChange(situacao)}
                            disabled={enviando}
                          >
                            {ROTULOS_STATUS_FINANCEIRO[situacao]}
                          </Chip>
                        ))}
                      </GrupoDeChips>
                    )}
                  />

                  <div className="mb-4 grid grid-cols-2 gap-4">
                    <Controller
                      control={form.control}
                      name={caminhoDoItem(indice, 'dataCompra')}
                      render={({ field, fieldState }) => (
                        <CampoDoPedido
                          rotulo="DATA DO LANÇAMENTO"
                          htmlFor={`item-${n}-data-compra`}
                          erro={fieldState.error?.message}
                        >
                          <CampoMascarado
                            id={`item-${n}-data-compra`}
                            aria-label={`Data do lançamento do item ${n}`}
                            formato="data"
                            value={textoDoCampo(field.value)}
                            onChange={field.onChange}
                            disabled={enviando}
                            className={cn(EDITAVEL, 'font-sans')}
                          />
                        </CampoDoPedido>
                      )}
                    />
                    <Controller
                      control={form.control}
                      name={caminhoDoItem(indice, 'desconto')}
                      render={({ field, fieldState }) => (
                        <CampoDoPedido
                          rotulo="DESCONTO (R$)"
                          htmlFor={`item-${n}-desconto`}
                          erro={fieldState.error?.message}
                        >
                          <CampoMascarado
                            id={`item-${n}-desconto`}
                            aria-label={`Desconto do item ${n}`}
                            formato="dinheiro"
                            placeholder="0,00"
                            value={textoDoCampo(field.value)}
                            onChange={field.onChange}
                            disabled={enviando}
                            className={cn(EDITAVEL, 'text-right font-sans')}
                          />
                        </CampoDoPedido>
                      )}
                    />
                    <Controller
                      control={form.control}
                      name={caminhoDoItem(indice, 'acrescimo')}
                      render={({ field, fieldState }) => (
                        <CampoDoPedido
                          rotulo="ACRÉSCIMO (R$)"
                          htmlFor={`item-${n}-acrescimo`}
                          erro={fieldState.error?.message}
                        >
                          <CampoMascarado
                            id={`item-${n}-acrescimo`}
                            aria-label={`Acréscimo do item ${n}`}
                            formato="dinheiro"
                            placeholder="0,00"
                            value={textoDoCampo(field.value)}
                            onChange={field.onChange}
                            disabled={enviando}
                            className={cn(EDITAVEL, 'text-right font-sans')}
                          />
                        </CampoDoPedido>
                      )}
                    />
                  </div>

                  <div className="mb-4">
                    <span className="mb-2 block font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
                      VALOR FINAL DO LANÇAMENTO
                    </span>
                    <span className="block font-mono text-[20px] font-semibold text-brass-light">
                      {formatarDinheiro(valorFinal)}
                    </span>
                  </div>

                  <div className="mt-1 border-t border-linha-forte pt-3.5">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`item-${n}-parcelar`}
                        checked={parcelado}
                        onCheckedChange={(marcado) => {
                          form.setValue(
                            caminhoDoItem(indice, 'parcelas'),
                            marcado === true ? '2' : '',
                            {
                              shouldDirty: true,
                            },
                          );
                          form.setValue(
                            caminhoDoItem(indice, 'intervaloParcelamento'),
                            marcado === true ? 'mensal' : undefined,
                            { shouldDirty: true },
                          );
                        }}
                        disabled={enviando}
                        aria-label={`Parcelar o lançamento do item ${n}`}
                      />
                      <Label htmlFor={`item-${n}-parcelar`}>Parcelar este lançamento</Label>
                    </div>

                    {parcelado && (
                      <div className="mt-2.5 flex flex-wrap gap-4">
                        <Controller
                          control={form.control}
                          name={caminhoDoItem(indice, 'parcelas')}
                          render={({ field, fieldState }) => (
                            <CampoDoPedido
                              rotulo="Nº DE PARCELAS"
                              htmlFor={`item-${n}-parcelas`}
                              erro={fieldState.error?.message}
                              className="w-[140px]"
                            >
                              <Input
                                id={`item-${n}-parcelas`}
                                aria-label={`Parcelas do item ${n}`}
                                type="number"
                                min={2}
                                max={60}
                                name={field.name}
                                ref={field.ref}
                                onBlur={field.onBlur}
                                value={textoDoCampo(field.value)}
                                onChange={(evento) => field.onChange(evento.target.value)}
                                disabled={enviando}
                                className={EDITAVEL}
                              />
                            </CampoDoPedido>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name={caminhoDoItem(indice, 'intervaloParcelamento')}
                          render={({ field, fieldState }) => (
                            <CampoDoPedido
                              rotulo="INTERVALO"
                              htmlFor={`item-${n}-intervalo`}
                              erro={fieldState.error?.message}
                              className="w-[200px]"
                            >
                              <Select
                                value={field.value ?? ''}
                                onValueChange={field.onChange}
                                disabled={enviando}
                              >
                                <SelectTrigger
                                  id={`item-${n}-intervalo`}
                                  aria-label={`Intervalo das parcelas do item ${n}`}
                                  className={cn(EDITAVEL, 'w-full')}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {INTERVALO_PARCELAMENTO.map((valor) => (
                                    <SelectItem key={valor} value={valor}>
                                      {ROTULOS_INTERVALO[valor]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </CampoDoPedido>
                          )}
                        />
                      </div>
                    )}
                  </div>
                </CartaoDoFormulario>
              );
            })
          )}

          <CartaoDoFormulario titulo="Observações">
            <CampoDoPedido
              rotulo="OBSERVAÇÃO"
              htmlFor="pedido-observacao"
              erro={erros.observacao?.message}
            >
              <Controller
                control={form.control}
                name="observacao"
                render={({ field }) => (
                  <Textarea
                    id="pedido-observacao"
                    rows={2}
                    placeholder="Notas internas sobre o pedido"
                    {...field}
                    value={field.value ?? ''}
                    disabled={enviando}
                    className={EDITAVEL}
                  />
                )}
              />
            </CampoDoPedido>
          </CartaoDoFormulario>

          {temDecisao && (
            <CartaoDoFormulario titulo="Aprovação">
              <div
                className={cn(
                  'rounded-controle px-3 py-2.5 font-sans text-sm',
                  SOMENTE_LEITURA,
                )}
              >
                {textoDaDecisao}
              </div>
              <p className="mt-1 text-[11px] text-texto-fraco">
                🔒 Resposta registrada pelas ações de aprovação/reprovação e de registro de
                compra no kanban.
              </p>
              <ErroDoCampo
                mensagem={erros.dataAprovacao?.message ?? erros.motivoReprovacao?.message}
              />
            </CartaoDoFormulario>
          )}

          {erroGeral !== null && (
            <p role="alert" className="mb-2 text-sm text-perigo">
              {erroGeral}
            </p>
          )}

          <div className="mt-2 flex flex-wrap justify-start gap-3 print:hidden">
            <Button type="submit" disabled={enviando}>
              {enviando ? 'Salvando…' : 'Salvar pedido de compra'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancelar} disabled={enviando}>
              Cancelar
            </Button>
          </div>
        </form>

        <CaixaDeAprovacao
          aberta={caixa === 'aprovacao'}
          ocupada={false}
          erro={null}
          onConfirmar={handleAprovado}
          onReprovar={() => setCaixa('reprovacao')}
          onCancelar={() => setCaixa('nenhuma')}
        />
        <CaixaDeReprovacao
          aberta={caixa === 'reprovacao'}
          ocupada={false}
          erro={null}
          onConfirmar={handleReprovado}
          onCancelar={() => setCaixa('nenhuma')}
        />
        <CaixaDeCompra
          aberta={caixa === 'compra'}
          ocupada={false}
          erro={erroDaCaixa}
          onConfirmar={handleComprado}
          onCancelar={() => setCaixa('nenhuma')}
        />
      </>
    );
  },
);
